import axios from "axios";
import type { DefaultCauseSet } from "@/interfaces/defaultCauses";
import { apiClient } from "@/lib/apiClient";

/** Rethrows the server's error message (not a bare axios error) so the UI can show it. */
function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    throw new Error(error.response.data.error as string);
  }
  throw new Error(fallback);
}

/** Every default-cause rotation, newest first. GFG super-admin only, gated server-side. */
export async function fetchDefaultCauseSets(): Promise<DefaultCauseSet[]> {
  const { data } = await apiClient.get<{ success: boolean; data: DefaultCauseSet[] }>(
    "/admin/system/default-causes",
  );
  return data.data;
}

/**
 * Open a new default list from `effectiveFrom` (YYYY-MM-DD), closing the current one.
 *
 * Ids only — the even split is the server's to derive, so there is one place that decides a share.
 */
export async function createDefaultCauseSet(input: {
  effectiveFrom: string;
  causeProductIds: string[];
  note?: string;
}): Promise<DefaultCauseSet> {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: DefaultCauseSet }>(
      "/admin/system/default-causes",
      input,
    );
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't save the new cause list. Please try again.");
  }
}

/** Replace the charities on a list that has not ended. Ids only; the split is derived server-side. */
export async function updateDefaultCauseSet(
  id: number,
  causeProductIds: string[],
): Promise<DefaultCauseSet> {
  try {
    const { data } = await apiClient.put<{ success: boolean; data: DefaultCauseSet }>(
      `/admin/system/default-causes/${id}`,
      { causeProductIds },
    );
    return data.data;
  } catch (error) {
    rethrowApiError(error, "Couldn't update the cause list. Please try again.");
  }
}

/** Remove a list that hasn't started. Refused server-side once it is in force. */
export async function deleteDefaultCauseSet(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/system/default-causes/${id}`);
  } catch (error) {
    rethrowApiError(error, "Couldn't delete the cause list. Please try again.");
  }
}
